import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useTenant } from "../hooks/useTenant";
import IntegrationService from "../services/integration_service";

export const AlertContext = createContext({
    alerts: [],
    unreadCount: 0,
    dismissAlert: () => {},
    clearAll: () => {},
});

const POLL_INTERVAL = 30000; // 30 seconds

/**
 * AlertProvider
 * Monitora integrações (Evolution API) e gera alertas globais.
 */
export const AlertProvider = ({ children }) => {
    const { idTenant } = useTenant();
    const [alerts, setAlerts] = useState([]);
    const pollingRef = useRef(null);
    const prevStatusRef = useRef(null);

    const addAlert = useCallback((alert) => {
        setAlerts((prev) => {
            // Evita duplicatas por key
            const filtered = prev.filter((a) => a.key !== alert.key);
            return [{ ...alert, id: Date.now(), read: false, timestamp: new Date() }, ...filtered];
        });
    }, []);

    const removeAlertByKey = useCallback((key) => {
        setAlerts((prev) => prev.filter((a) => a.key !== key));
    }, []);

    const checkEvolutionStatus = useCallback(async () => {
        if (!idTenant) return;
        try {
            const integrations = await IntegrationService.getAll(idTenant);
            const evolution = integrations.find((i) => i.provider === "evolution");

            if (!evolution?.isActive || !evolution?.config?.apiKey || !evolution?.config?.apiUrl || !evolution?.config?.instanceName) {
                prevStatusRef.current = null;
                return;
            }

            const { apiUrl, apiKey, instanceName } = evolution.config;
            const baseUrl = apiUrl.replace(/\/$/, "");

            const res = await fetch(`${baseUrl}/instance/connectionState/${instanceName}`, {
                method: "GET",
                headers: { apikey: apiKey },
            });

            const data = await res.json();
            const state = res.ok ? (data?.instance?.state || data?.state || "error") : "error";

            // Gera alerta quando muda para offline/error
            if (state !== "open" && prevStatusRef.current === "open") {
                addAlert({
                    key: "evolution-offline",
                    type: "danger",
                    icon: "mdi-whatsapp",
                    title: "Evolution API Offline",
                    message: `A instância "${instanceName}" ficou offline. Mensagens de WhatsApp não serão enviadas.`,
                    link: "/settings/integrations",
                    linkText: "Ver Integrações",
                });
            }

            // Remove alerta quando volta a ficar online
            if (state === "open" && prevStatusRef.current !== "open" && prevStatusRef.current !== null) {
                removeAlertByKey("evolution-offline");
                addAlert({
                    key: "evolution-online",
                    type: "success",
                    icon: "mdi-whatsapp",
                    title: "Evolution API Online",
                    message: `A instância "${instanceName}" voltou a ficar online.`,
                    autoRemove: 10000,
                });
            }

            prevStatusRef.current = state;
        } catch {
            if (prevStatusRef.current === "open") {
                addAlert({
                    key: "evolution-offline",
                    type: "danger",
                    icon: "mdi-whatsapp",
                    title: "Evolution API Offline",
                    message: "Não foi possível verificar o status da instância. Verifique a conexão.",
                    link: "/settings/integrations",
                    linkText: "Ver Integrações",
                });
            }
            prevStatusRef.current = "error";
        }
    }, [idTenant, addAlert, removeAlertByKey]);

    // Polling
    useEffect(() => {
        if (!idTenant) return;

        // Delay de 5s para não bloquear o boot do app
        const timeout = setTimeout(() => {
            checkEvolutionStatus();
            pollingRef.current = setInterval(checkEvolutionStatus, POLL_INTERVAL);
        }, 5000);

        return () => {
            clearTimeout(timeout);
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [idTenant, checkEvolutionStatus]);

    // Auto-remove de alertas temporários
    useEffect(() => {
        const autoRemoveAlerts = alerts.filter((a) => a.autoRemove);
        autoRemoveAlerts.forEach((alert) => {
            const timer = setTimeout(() => {
                setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
            }, alert.autoRemove);
            return () => clearTimeout(timer);
        });
    }, [alerts]);

    const dismissAlert = useCallback((alertId) => {
        setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    }, []);

    const markAsRead = useCallback(() => {
        setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
    }, []);

    const clearAll = useCallback(() => {
        setAlerts([]);
    }, []);

    const unreadCount = alerts.filter((a) => !a.read).length;

    return (
        <AlertContext.Provider value={{ alerts, unreadCount, dismissAlert, markAsRead, clearAll }}>
            {children}
        </AlertContext.Provider>
    );
};
